<section
  (dragover)="acceptDrag($event)"
  (dragleave)="leaveDrag($event)"
  (drop)="dropFiles($event)"
  [attr.aria-labelledby]="titleId"
  [class.proffeo-file-explorer-drop-active]="dropActive()"
  class="proffeo-file-explorer flex flex-col"
>
  <h2
    [id]="titleId"
    [class.sr-only]="!selectionMode()"
    class="proffeo-file-explorer-title px-3 pt-3"
  >
    {{ titleKey() | translate }}
  </h2>

  <div class="flex min-w-0 flex-wrap items-center gap-2 pb-2">
    <div class="flex shrink-0 items-center gap-2">
      <proffeo-ui-button
        (clicked)="goBack()"
        [ariaLabel]="'files.explorer.back' | translate"
        [tooltip]="'files.explorer.back'"
        [disabled]="!canGoBack()"
        fontIcon="chevron_backward"
      />
      <proffeo-ui-button
        (clicked)="goUp()"
        [ariaLabel]="'files.explorer.up' | translate"
        [tooltip]="'files.explorer.up'"
        [disabled]="!canGoUp()"
        fontIcon="arrow_upward"
      />
    </div>
    <proffeo-ui-input
      [(value)]="searchQuery"
      [clearable]="true"
      variant="search"
      placeholder="files.explorer.search"
      class="min-w-0 flex-1 basis-64"
    />
    <div class="flex shrink-0 flex-wrap items-center gap-2">
      @if (canEditExternalLink()) {
        <proffeo-ui-button
          (clicked)="editExternalLink()"
          [disabled]="mutationPending()"
          [ariaLabel]="'files.explorer.external-edit' | translate"
          tooltip="files.explorer.external-edit"
          fontIcon="link"
        />
      }
      <proffeo-ui-button
        (clicked)="retry()"
        [ariaLabel]="'files.explorer.refresh' | translate"
        [disabled]="currentState().loading"
        tooltip="files.explorer.refresh"
        fontIcon="refresh"
      />
      @if (!selectionMode() && currentFolder()?.canUploadFiles) {
        <input
          #uploadInput
          (change)="uploadSelectedFiles($event)"
          type="file"
          multiple
          class="hidden"
        />
        <proffeo-ui-button
          (clicked)="openUploadPicker(uploadInput)"
          [disabled]="mutationPending()"
          [ariaLabel]="'files.explorer.upload' | translate"
          tooltip="files.explorer.upload"
          fontIcon="upload_file"
        />
      }
      @if (!selectionMode() && currentFolder()?.canCreateFolder) {
        <proffeo-ui-button
          (clicked)="createFolder()"
          [disabled]="mutationPending()"
          text="files.explorer.create-folder"
          fontIcon="create_new_folder"
          variant="accent"
        />
      }
    </div>
  </div>

  @if (canGoUp()) {
    <nav
      [attr.aria-label]="'files.explorer.breadcrumb' | translate"
      class="flex min-w-0 items-center gap-1 pb-2"
    >
      @for (crumb of breadcrumbTrail(); track crumb.id ?? 'root'; let last = $last) {
        @if (last) {
          <span
            aria-current="page"
            class="proffeo-ui-text truncate"
          >
            {{ crumb.name }}
          </span>
        } @else {
          <button
            (click)="navigateTo(crumb.id)"
            type="button"
            class="proffeo-file-explorer-crumb proffeo-ui-focus-ring truncate"
          >
            @if (crumb.id === null) {
              {{ 'files.explorer.root' | translate }}
            } @else {
              {{ crumb.name }}
            }
          </button>
          <mat-icon
            aria-hidden="true"
            fontIcon="chevron_forward"
            class="proffeo-ui-icon-muted icon-sm shrink-0"
          />
        }
      }
    </nav>
  }

  @if (!selectionMode()) {
    @if (externalLink(); as link) {
      <div class="proffeo-file-explorer-strip flex flex-wrap items-center gap-x-2 gap-y-0.5 px-3 py-1.5">
        <a
          [href]="link.url"
          [title]="link.host"
          target="_blank"
          rel="noopener noreferrer"
          class="proffeo-file-explorer-link proffeo-ui-focus-ring inline-flex items-center gap-1"
        >
          <mat-icon
            aria-hidden="true"
            fontIcon="open_in_new"
            class="icon-sm"
          />
          {{ 'files.explorer.external-open' | translate }}
          <span class="proffeo-file-explorer-muted">({{ link.host }})</span>
        </a>
        <span class="proffeo-file-explorer-muted">{{ 'files.explorer.external-link-only' | translate }}</span>
      </div>
    }
  }

  @if (!selectionMode() && needsInitialization()) {
    <div class="px-3 py-2">
      <proffeo-ui-button
        (clicked)="initialize()"
        [disabled]="initializing()"
        text="files.explorer.initialize"
        variant="accent"
        size="sm"
      />
    </div>
  }
  @if (mutationError()) {
    <p
      role="alert"
      class="proffeo-file-explorer-error px-3 py-1.5"
    >
      {{ 'files.explorer.operation-error' | translate }}
    </p>
  }
  @if (!selectionMode()) {
    <proffeo-file-upload-list
      (cancelRequested)="cancelUpload($event)"
      (dismiss)="dismissUpload($event)"
      [uploads]="activeUploads()"
      class="px-2"
    />
  }

  @switch (viewState()) {
    @case ('not-initialized') {
      <p class="proffeo-file-explorer-message px-3 py-6">{{ 'files.explorer.not-initialized' | translate }}</p>
    }
    @case ('loading') {
      <p
        aria-live="polite"
        class="proffeo-file-explorer-message px-3 py-6"
      >
        {{ 'files.explorer.loading' | translate }}
      </p>
    }
    @case ('error') {
      <div class="flex items-center gap-2 px-3 py-4">
        <p class="proffeo-file-explorer-error">{{ 'files.explorer.load-error' | translate }}</p>
        <proffeo-ui-button
          (clicked)="retry()"
          text="files.explorer.retry"
          variant="ghost"
          size="sm"
        />
      </div>
    }
    @case ('loaded') {
      @if (rows().length === 0) {
        <p class="proffeo-file-explorer-message px-3 py-6">{{ emptyKey() | translate }}</p>
      } @else {
        <div class="proffeo-file-explorer-scroll proffeo-ui-border-default overflow-x-auto">
          <table
            [attr.aria-label]="titleKey() | translate"
            [class.proffeo-file-explorer-table-selection]="selectionMode()"
            role="treegrid"
            class="proffeo-file-explorer-table w-full"
          >
            <colgroup>
              <col />
              <col class="proffeo-file-explorer-col-date" />
              @if (!selectionMode()) {
                <col class="proffeo-file-explorer-col-size" />
                <col class="proffeo-file-explorer-col-kind" />
                <col class="proffeo-file-explorer-col-actions" />
              }
            </colgroup>
            <thead>
              <tr>
                <th
                  [attr.aria-sort]="ariaSort().name"
                  scope="col"
                >
                  <button
                    (click)="sortByColumn('name')"
                    type="button"
                    class="proffeo-file-explorer-sort proffeo-ui-focus-ring"
                  >
                    {{ 'files.explorer.columns.name' | translate }}
                    @if (ariaSort().name !== 'none') {
                      <mat-icon
                        [fontIcon]="ariaSort().name === 'ascending' ? 'keyboard_arrow_up' : 'keyboard_arrow_down'"
                        aria-hidden="true"
                        class="icon-sm"
                      />
                    }
                  </button>
                </th>
                <th
                  [attr.aria-sort]="ariaSort().date"
                  scope="col"
                >
                  <button
                    (click)="sortByColumn('date')"
                    type="button"
                    class="proffeo-file-explorer-sort proffeo-ui-focus-ring"
                  >
                    {{ 'files.explorer.columns.added' | translate }}
                    @if (ariaSort().date !== 'none') {
                      <mat-icon
                        [fontIcon]="ariaSort().date === 'ascending' ? 'keyboard_arrow_up' : 'keyboard_arrow_down'"
                        aria-hidden="true"
                        class="icon-sm"
                      />
                    }
                  </button>
                </th>
                @if (!selectionMode()) {
                  <th
                    [attr.aria-sort]="ariaSort().size"
                    scope="col"
                    class="proffeo-file-explorer-numeric"
                  >
                    <button
                      (click)="sortByColumn('size')"
                      type="button"
                      class="proffeo-file-explorer-sort proffeo-ui-focus-ring"
                    >
                      {{ 'files.explorer.columns.size' | translate }}
                      @if (ariaSort().size !== 'none') {
                        <mat-icon
                          [fontIcon]="ariaSort().size === 'ascending' ? 'keyboard_arrow_up' : 'keyboard_arrow_down'"
                          aria-hidden="true"
                          class="icon-sm"
                        />
                      }
                    </button>
                  </th>
                  <th scope="col">
                    <span class="proffeo-file-explorer-sort">{{ 'files.explorer.columns.kind' | translate }}</span>
                  </th>
                  <th scope="col">
                    <span class="sr-only">{{ 'files.explorer.columns.actions' | translate }}</span>
                  </th>
                }
              </tr>
            </thead>
            <tbody>
              @for (row of rows(); track row.key) {
                @if (row.type === 'status') {
                  <tr
                    [attr.aria-level]="row.depth + 1"
                    class="proffeo-file-explorer-status-row"
                  >
                    <td [attr.colspan]="selectionMode() ? 2 : 5">
                      <div
                        [style.padding-inline-start.rem]="row.indentRem + 1.5"
                        class="flex items-center gap-2"
                      >
                        @if (row.status === 'loading') {
                          <span
                            aria-live="polite"
                            class="proffeo-file-explorer-muted"
                          >
                            {{ 'files.explorer.loading' | translate }}
                          </span>
                        } @else {
                          <span class="proffeo-file-explorer-error">{{ 'files.explorer.load-error' | translate }}</span>
                          <proffeo-ui-button
                            (clicked)="retryExpansion(row.folderId)"
                            text="files.explorer.retry"
                            variant="ghost"
                            size="sm"
                          />
                        }
                      </div>
                    </td>
                  </tr>
                }
                @if (row.type !== 'status') {
                  <tr
                    (click)="selectRow(row, $event)"
                    (dblclick)="openRowFromPointer(row, $event)"
                    (keydown)="handleRowKeydown($event, row)"
                    [attr.data-row-key]="row.key"
                    [attr.tabindex]="row.key === focusKey() ? 0 : -1"
                    [attr.aria-level]="row.depth + 1"
                    [attr.aria-selected]="row.key === selectedKey()"
                    [attr.aria-expanded]="row.type === 'folder' && row.expandable ? row.expanded : null"
                    [class.proffeo-file-explorer-row-selected]="row.key === selectedKey()"
                    class="proffeo-file-explorer-row"
                  >
                    <td>
                      <div
                        [style.padding-inline-start.rem]="row.indentRem"
                        class="flex min-w-0 items-center gap-1.5"
                      >
                        @if (row.type === 'folder' && row.expandable) {
                          <button
                            (click)="toggleFolder(row)"
                            [attr.aria-expanded]="row.expanded"
                            [attr.aria-label]="
                              (row.expanded ? 'files.explorer.collapse' : 'files.explorer.expand')
                                | translate: { name: row.name }
                            "
                            [class.proffeo-file-explorer-disclosure-open]="row.expanded"
                            type="button"
                            class="proffeo-file-explorer-disclosure proffeo-ui-focus-ring shrink-0"
                          >
                            <mat-icon
                              aria-hidden="true"
                              fontIcon="chevron_forward"
                              class="icon-sm"
                            />
                          </button>
                        } @else if (row.type === 'folder' && selectionMode()) {
                          <button
                            (click)="openRow(row)"
                            [attr.aria-label]="'files.explorer.open-folder' | translate"
                            type="button"
                            class="proffeo-file-explorer-disclosure proffeo-ui-focus-ring shrink-0"
                          >
                            <mat-icon
                              aria-hidden="true"
                              fontIcon="folder_open"
                              class="icon-sm"
                            />
                          </button>
                        } @else {
                          <span
                            aria-hidden="true"
                            class="proffeo-file-explorer-disclosure-spacer shrink-0"
                          ></span>
                        }
                        <mat-icon
                          [fontIcon]="row.icon"
                          [attr.data-kind]="row.iconKind"
                          aria-hidden="true"
                          class="proffeo-file-explorer-icon icon-sm shrink-0"
                        />
                        <span
                          [title]="row.name"
                          class="proffeo-file-explorer-name truncate"
                        >
                          {{ row.name }}
                        </span>
                      </div>
                    </td>
                    <td class="proffeo-file-explorer-meta">{{ row.dateLabel }}</td>
                    @if (!selectionMode()) {
                      <td class="proffeo-file-explorer-meta proffeo-file-explorer-numeric">{{ row.sizeLabel }}</td>
                      <td class="proffeo-file-explorer-meta truncate">{{ row.kindKey | translate }}</td>
                      <td>
                        <div class="proffeo-file-explorer-actions flex items-center justify-end">
                          @if (row.type === 'folder') {
                            <proffeo-ui-button
                              (clicked)="openRow(row)"
                              [ariaLabel]="'files.explorer.open-folder' | translate"
                              fontIcon="folder_open"
                              variant="ghost"
                              size="sm"
                            />
                            @if (row.canRename) {
                              <proffeo-ui-button
                                (clicked)="renameFolder(row.row.folder)"
                                [disabled]="mutationPending()"
                                [ariaLabel]="'files.explorer.rename' | translate"
                                fontIcon="edit"
                                variant="ghost"
                                size="sm"
                              />
                            }
                            @if (row.canDelete) {
                              <proffeo-ui-button
                                (clicked)="deleteFolder(row.row.folder)"
                                [disabled]="mutationPending()"
                                [ariaLabel]="'files.explorer.delete-folder' | translate"
                                fontIcon="delete"
                                variant="ghost"
                                size="sm"
                              />
                            }
                          }
                          @if (row.type === 'file') {
                            @if (row.nested) {
                              <proffeo-ui-button
                                (clicked)="showInFolder(row)"
                                [ariaLabel]="'files.explorer.show-in-folder' | translate"
                                fontIcon="folder_open"
                                variant="ghost"
                                size="sm"
                              />
                            }
                            @if (row.capabilities.canRename) {
                              <proffeo-ui-button
                                (clicked)="renameFile(row.file.id)"
                                [disabled]="mutationPending()"
                                [ariaLabel]="'files.explorer.rename' | translate"
                                fontIcon="edit"
                                variant="ghost"
                                size="sm"
                              />
                            }
                            @if (row.capabilities.canMove) {
                              <proffeo-ui-button
                                (clicked)="moveFile(row.file.id)"
                                [disabled]="mutationPending()"
                                [ariaLabel]="'files.explorer.move' | translate"
                                fontIcon="drive_file_move"
                                variant="ghost"
                                size="sm"
                              />
                            }
                            @if (row.capabilities.canDelete) {
                              <proffeo-ui-button
                                (clicked)="deleteFile(row.file.id)"
                                [disabled]="mutationPending()"
                                [ariaLabel]="'files.explorer.delete-file' | translate"
                                fontIcon="delete"
                                variant="ghost"
                                size="sm"
                              />
                            }
                            @if (row.canSetAsAvatar) {
                              <proffeo-ui-button
                                (clicked)="onSetAsAvatar(row.file.id)"
                                [ariaLabel]="'files.files-list-set-avatar' | translate"
                                fontIcon="account_circle"
                                variant="ghost"
                                size="sm"
                              />
                            }
                            @if (row.viewable) {
                              <proffeo-ui-button
                                (clicked)="previewRow(row)"
                                [ariaLabel]="'files.explorer.preview' | translate"
                                fontIcon="visibility"
                                variant="ghost"
                                size="sm"
                              />
                            }
                            <proffeo-ui-button
                              (clicked)="downloadRow(row)"
                              [ariaLabel]="'files.explorer.download' | translate"
                              fontIcon="download"
                              variant="ghost"
                              size="sm"
                            />
                          }
                        </div>
                      </td>
                    }
                  </tr>
                }
              }
            </tbody>
          </table>
        </div>
      }
    }
  }

  @if (selectionMode() && currentFolder()?.canUploadFiles) {
    <div class="flex justify-end px-3 py-2">
      <proffeo-ui-button
        (clicked)="selectFolder()"
        text="files.explorer.select-folder"
        variant="accent"
      />
    </div>
  }
</section>
